import { isUsingSkill } from "./actorbattle";
import { FOOT_COMBO_STEPS, LOOPED_COMBO_STEP, type ClipEntry, type ClipName } from "../render/creaturemesh";

/** `iLevel = MIN(iLevel, 2)` (`PythonPlayerSkill.cpp:932`). */
const COMBO_SKILL_TOP_TYPE = 2;

/** `__GetCurrentComboType` (`ActorInstance.cpp:164-174`) for the player's own
 *  body: the first type mounted, else the combo skill's level capped at the
 *  third while the skill is toggled on (`SetComboSkillFlag`,
 *  `PythonPlayerSkill.cpp:916-945`). A bow or bare hands start no combo chain
 *  at all (`comboChainOf`). */
export function currentComboType(riding: boolean, comboLevel: number, comboActive: boolean): number {
  if (riding) return 0; // ActorInstance.cpp:170-171
  return comboActive && comboLevel > 0 ? Math.min(comboLevel, COMBO_SKILL_TOP_TYPE) : 0; // PythonPlayerSkill.cpp:925-941
}

/** `GetMotionDuration()*0.9f`, where a step's chain opens without `ComboInputData` (`ActorInstanceBattle.cpp:202`). */
const NO_COMBO_INPUT_RATIO = 0.9;

/** `InputComboAttackCommand` (`ActorInstanceBattle.cpp:156-213`) for a click
 *  `elapsedMs` of wall time after step `previousStep` of a `steps`-long combo
 *  began, `playing` being that step's clip while it is still drawn: the step the
 *  click plays, or null for a click that plays none now. A `mounted` combo
 *  follows its last step with its second. With `preInput` held (see
 *  `comboPreInput`), `ComboProcess` plays the next step as soon as the direct
 *  input time has passed, whatever the input limit. */
export function comboClickStep(
  previousStep: number, playing: ClipEntry | null, elapsedMs: number, attackSpeed: number,
  steps = FOOT_COMBO_STEPS, mounted = false, preInput = false,
): number | null {
  if (previousStep === 0 || playing === null) return 1; // ActorInstanceBattle.cpp:166-170, cleared as the step gives way (ActorInstanceMotion.cpp:751-757)
  const motionS = motionSeconds(elapsedMs, attackSpeed);
  if (motionS >= playing.durationS) return 1; // the step returned to wait and __ClearCombo zeroed the index (ActorInstanceBattle.cpp:309-314)
  const open = preInput && playing.directInputS !== undefined
    ? motionS > playing.directInputS // ComboProcess (ActorInstanceBattle.cpp:227-239)
    : playing.directInputS === undefined || playing.inputLimitS === undefined
      ? motionS >= playing.durationS * NO_COMBO_INPUT_RATIO // ActorInstanceBattle.cpp:199-209
      : motionS <= playing.inputLimitS && motionS >= playing.directInputS; // :177-191
  if (!open) return null;
  if (previousStep < steps) return previousStep + 1;
  return mounted ? LOOPED_COMBO_STEP : null; // ++m_dwcurComboIndex past the combo plays nothing (:257, :280-285), unless __OnEndCombo looped it (:299-305)
}

/** Whether an attack input `elapsedMs` into step `previousStep`, `playing`
 *  still drawn, is held over as `m_isPreInput` (`ActorInstanceBattle.cpp:192-197`):
 *  after the step's pre-input time, not yet past its direct input time, and not
 *  past its input limit (`:177-183`). A held pre-input takes no further input
 *  (`:160-161`) and plays the next step through `comboClickStep`. */
export function comboPreInput(
  previousStep: number, playing: ClipEntry | null, elapsedMs: number, attackSpeed: number,
): boolean {
  if (previousStep === 0 || playing === null) return false; // ActorInstanceBattle.cpp:166-170
  const { preInputS, directInputS, inputLimitS } = playing;
  if (preInputS === undefined || directInputS === undefined || inputLimitS === undefined) return false; // IsComboInputTimeData (:171)
  const motionS = motionSeconds(elapsedMs, attackSpeed);
  return motionS <= inputLimitS && motionS <= directInputS && motionS > preInputS; // :177-197
}

/** `GetAttackingElapsedTime` (`ActorInstanceBattle.cpp:451-455`): wall time
 *  scaled by the motion's speed, `POINT_ATT_SPEED / 100`. */
function motionSeconds(elapsedMs: number, attackSpeed: number): number {
  return (elapsedMs / 1_000) * (Math.max(1, attackSpeed) / 100);
}

/** One attack beat of the player's own combo, `ComboProcess` and
 *  `InputComboAttackCommand` (`ActorInstanceBattle.cpp:156-239`) together:
 *  `previous` the step last played and when (null from rest), `playing` its
 *  clip's pack row, `drawn` the clip the body is drawn in now, `takesInput`
 *  whether an attack input is held and may act. Answers the step the beat
 *  plays (1 to start over, null for none) and whether a pre-input is held
 *  after it. */
export function comboBeat(
  previous: { readonly step: number; readonly lastMs: number } | null,
  playing: ClipEntry | null, drawn: ClipName | null, nowMs: number,
  attackSpeed: number, steps: number, mounted: boolean, preInput: boolean, takesInput: boolean,
): { readonly step: number | null; readonly preInput: boolean } {
  const entry = previous === null ? null : playingComboEntry(playing, drawn);
  const elapsedMs = previous === null ? 0 : nowMs - previous.lastMs;
  let held = entry === null ? false : preInput; // __ClearCombo (ActorInstanceBattle.cpp:309-314)
  if (takesInput && !held) held = comboPreInput(previous?.step ?? 0, entry, elapsedMs, attackSpeed);
  const step = comboClickStep(previous?.step ?? 0, entry, elapsedMs, attackSpeed, steps, mounted, held);
  return { step, preInput: step === 1 ? false : held };
}

/** The step still playing, read on the motion's own clock rather than off the
 *  frame last drawn, which lags a slow renderer by a whole frame: only a body
 *  drawn in a skill motion, which carries no combo data, has left it
 *  (`__SetMotion`, `ActorInstanceMotion.cpp:751-757`). */
function playingComboEntry(playing: ClipEntry | null, drawn: ClipName | null): ClipEntry | null {
  return isUsingSkill(drawn) ? null : playing;
}
